import Vue from 'vue'

// Track direction via Navigation API (fires before beforeEach for back/forward traversals)
let _isBack = false
if (typeof window !== 'undefined' && window.navigation) {
  window.navigation.addEventListener('navigate', (event) => {
    _isBack = event.navigationType === 'traverse' &&
      event.destination.index < window.navigation.currentEntry.index
  })
}

// Active transition context
let _ctx = null  // { transitionItemId, projectId, sourceEl, sourceProjEl, resolve }

function esc(id) { return CSS.escape(String(id)) }

/**
 * Called by router beforeEach BEFORE startViewTransition.
 * Imperatively sets view-transition-name on source elements so they're
 * captured in the old state snapshot.
 */
export function startTransition({ transitionItemId, projectId, resolve, isBack: isBackParam = null }) {
  // Prefer the explicitly-passed direction (derived from route names in the router).
  // Fall back to the Navigation API value only when the router didn't pass one.
  // The Navigation API doesn't fire for SPA pushState navigations, so _isBack can
  // get stuck as true after a back traversal if we rely on it alone.
  if (isBackParam !== null) _isBack = isBackParam
  cleanup()

  const sourceEl = transitionItemId
    ? (document.querySelector(`[data-item-id="${esc(transitionItemId)}"] img`)
      || document.querySelector('.ds-page .frame-renderer__thumbnail'))
    : null
  if (sourceEl) sourceEl.style.viewTransitionName = 'ds-item-active'

  const sourceProjEl = projectId
    ? (document.querySelector(`[data-project-id="${esc(projectId)}"] .project-lane__name`)
      || document.querySelector(`[data-vt-project-header="${esc(projectId)}"] .project-title`))
    : null
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = `project-header`

  const sourceThumbEls = []
  const sourceWsEls = []
  if (projectId && !transitionItemId) {
    const MAX_THUMBS = 20

    // querySelectorAll returns elements in DOM order natively — no sort needed.
    const p = `[data-project-id="${esc(projectId)}"]`
    const els = [...document.querySelectorAll(
      `${p} .ds-grid-thumb__img, ${p} .ds-spreadsheet-thumb, ${p} .ds-grid-group-header__text`
    )]

    let thumbCount = 0
    let thumbIdx = 0
    let wsIdx = 0
    const allAnimated = []
    for (const el of els) {
      if (el.tagName === 'IMG') {
        if (thumbCount < MAX_THUMBS) {
          el.style.viewTransitionName = `ds-thumb-${thumbIdx}`
          const entry = { el, vtName: `ds-thumb-${thumbIdx}`, itemId: el.closest('[data-item-id]')?.dataset.itemId, idx: thumbIdx, kind: 'thumb' }
          sourceThumbEls.push(entry)
          allAnimated.push(entry)
          thumbIdx++
          thumbCount++
        }
      } else {
        if (thumbCount < MAX_THUMBS) {
          el.style.viewTransitionName = `ds-ws-${wsIdx}`
          const entry = { el, vtName: `ds-ws-${wsIdx}`, wsText: el.textContent.trim(), idx: wsIdx, kind: 'ws' }
          sourceWsEls.push(entry)
          allAnimated.push(entry)
          wsIdx++
        }
      }
    }

    // allAnimated is already in DOM order — assign stagger delays directly.
    allAnimated.forEach((item, pos) => {
      document.documentElement.style.setProperty(
        item.kind === 'thumb' ? `--vt-delay-thumb-${item.idx}` : `--vt-delay-ws-${item.idx}`,
        `${pos * 20}ms`
      )
    })
    // Forward navigation only: give earlier-animating thumbs a higher z-index so
    // they render above thumbs that haven't started moving yet.
    // Backward keeps z-index unset (CSS falls back to 0 → DOM order, later = on top).
    if (!_isBack) {
      const total = allAnimated.length
      allAnimated.forEach((item, pos) => {
        if (item.kind === 'thumb') {
          document.documentElement.style.setProperty(`--vt-z-thumb-${item.idx}`, String(total - pos))
        }
      })
    }
  }

  _ctx = { transitionItemId, projectId, sourceEl, sourceProjEl, sourceThumbEls, sourceWsEls, resolve }
}

/**
 * Called by the destination component when its transition element is rendered.
 * Pass destEl explicitly to avoid keep-alive DOM collisions (scoped to component $el).
 */
export function signalReady(destEl = null) {
  if (!_ctx?.resolve) return
  const { transitionItemId, projectId, sourceEl, sourceProjEl, sourceThumbEls = [], sourceWsEls = [], resolve } = _ctx

  // Clear source names — old state was already captured by startViewTransition
  if (sourceEl) sourceEl.style.viewTransitionName = ''
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = ''
  sourceThumbEls.forEach(({ el }) => { el.style.viewTransitionName = '' })
  sourceWsEls.forEach(({ el }) => { el.style.viewTransitionName = '' })

  // Set destination item name (use passed element, or fall back to document query)
  const itemTarget = destEl || (transitionItemId
    ? (() => {
        const all = [...document.querySelectorAll(`[data-item-id="${esc(transitionItemId)}"] img`)]
        return all.find(el => el !== sourceEl) || null
      })()
    : null)
  if (itemTarget) itemTarget.style.viewTransitionName = 'ds-item-active'

  // Set destination project header name
  if (projectId) {
    const all = [...document.querySelectorAll(
      `[data-project-id="${esc(projectId)}"] .project-lane__name, [data-vt-project-header="${esc(projectId)}"] .project-title`
    )]
    const projTarget = all.find(el => el !== sourceProjEl) || null
    if (projTarget) projTarget.style.viewTransitionName = `project-header`
  }

  // Set destination thumbnail names
  const destThumbImgs = []
  if (sourceThumbEls.length) {
    const srcSet = new Set(sourceThumbEls.map(t => t.el))
    sourceThumbEls.forEach(({ vtName, itemId }) => {
      const all = [...document.querySelectorAll(`[data-item-id="${esc(itemId)}"] img`)]
      const dest = all.find(el => !srcSet.has(el))
      if (dest) {
        dest.style.viewTransitionName = vtName
        destThumbImgs.push(dest)
      }
    })
  }

  // Set destination workspace header names (absent when groupByWorkspace is off)
  if (sourceWsEls.length && projectId) {
    const srcSet = new Set(sourceWsEls.map(w => w.el))
    sourceWsEls.forEach(({ vtName, wsText }) => {
      const all = [...document.querySelectorAll(
        `[data-project-id="${esc(projectId)}"] .ds-grid-group-header__text`
      )]
      const dest = all.find(el => !srcSet.has(el) && el.textContent.trim() === wsText)
      if (dest) dest.style.viewTransitionName = vtName
    })
  }

  const fn = resolve
  _ctx.resolve = null  // prevent double-call

  // Wait for destination thumbnail images to paint before resolving so the VT
  // captures fully-rendered thumbnails. Already-cached images (complete=true) pass
  // through immediately; a 1s safety timeout prevents hanging on slow/failed loads.
  const pending = destThumbImgs.filter(img => !img.complete)
  if (pending.length) {
    Promise.race([
      Promise.all(pending.map(img => new Promise(r => {
        img.addEventListener('load', r, { once: true })
        img.addEventListener('error', r, { once: true })
      }))),
      new Promise(r => setTimeout(r, 1000)),
    ]).then(() => Vue.nextTick(fn))
  } else {
    Vue.nextTick(fn)
  }
}

/**
 * Called by router transition.finished to clear all imperative VT names.
 */
export function cleanup() {
  if (!_ctx) return
  const { sourceEl, sourceProjEl } = _ctx
  if (sourceEl) sourceEl.style.viewTransitionName = ''
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = ''
  // Sweep any ds-item-active or project-header-* names left on elements
  document.querySelectorAll('[style*="view-transition-name"]').forEach(el => {
    const vtn = el.style.viewTransitionName
    if (vtn && vtn !== 'none') el.style.viewTransitionName = ''
  })
  // Remove stagger delay and z-index custom properties set during this transition
  for (let i = 0; i < 20; i++) {
    document.documentElement.style.removeProperty(`--vt-delay-thumb-${i}`)
    document.documentElement.style.removeProperty(`--vt-delay-ws-${i}`)
    document.documentElement.style.removeProperty(`--vt-z-thumb-${i}`)
  }
  _ctx = null
}

export function getContext() { return _ctx }
export function isBack() { return _isBack }
